import { useState, useEffect, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { DashboardPage } from '../../components/PremiumDesign';
import { Button, SearchInput, ConfirmDeleteModal } from '@/components';
import { V2Table } from '@/common/components';
import {
  getCRMTasksAndFollowups,
  completeCRMContactTask,
  deleteCRMContactTask,
  deleteCRMContactFollowUp,
  pauseCRMContactFollowUp,
  resumeCRMContactFollowUp,
  stopCRMContactFollowUp,
  rescheduleCRMContactFollowUp,
} from '../api';
import { useDelayedSkeleton } from '@/common/hooks/useDelayedSkeleton';
import { getCompanyId } from '../utils/tasksAndFollowups';
import { CRMTaskModal } from './CRMTaskModal';
import { CRMFollowUpModal } from './CRMFollowUpModal';
import { CRMFollowUpDetailModal } from './CRMFollowUpDetailModal';
import { RescheduleModal } from './RescheduleModal';
import { StopFollowUpConfirmModal } from '../../components/StopFollowUpConfirmModal';
import { ActionConfirmModal } from '../../components/ActionConfirmModal';
import { parseApiError } from '@/common/utils';
import { buildTasksFollowupsColumns } from './tasksFollowupsColumns';

const PAGE_SIZE = 10;

const STAT_FILTERS = [
  { id: 'all', label: 'Total', dot: '#3B82F6' },
  { id: 'pending', label: 'Pending', dot: '#F97316' },
  { id: 'completed', label: 'Completed', dot: '#50AB3D' },
];

export function TasksAndFollowupsListView({ initialType = 'task' }) {
  const navigate = useNavigate();
  const location = useLocation();
  const queryClient = useQueryClient();
  const companyId = getCompanyId();

  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [status, setStatus] = useState('all');
  const [page, setPage] = useState(1);

  // Modals state
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isAddFollowUpModalOpen, setIsAddFollowUpModalOpen] = useState(false);
  const [taskToEdit, setTaskToEdit] = useState(null);
  const [taskToDelete, setTaskToDelete] = useState(null);
  const [followUpToDelete, setFollowUpToDelete] = useState(null);
  const [viewingFollowUpItem, setViewingFollowUpItem] = useState(null);
  const [actionPopup, setActionPopup] = useState(null);

  // Debounce search input by 400ms and reset page to 1
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const handleStatusChange = (newStatus) => {
    setStatus(newStatus);
    setPage(1);
  };

  // Fetch dynamic API data
  const {
    data,
    isLoading: isInitialLoading,
    isFetching,
    isError,
    refetch,
  } = useQuery({
    queryKey: [
      'crm-tasks-and-followups',
      companyId,
      initialType,
      status,
      debouncedSearch,
      page,
    ],
    queryFn: () => {
      const params = {
        type: initialType,
        page,
        page_size: PAGE_SIZE,
      };
      if (status) {
        params.status = status;
      }
      if (debouncedSearch) {
        params.search = debouncedSearch;
      }
      return getCRMTasksAndFollowups(companyId, params);
    },
    enabled: !!companyId,
    placeholderData: keepPreviousData,
  });

  const showDelayedSkeleton = useDelayedSkeleton(
    isFetching && data !== undefined,
    300,
    500,
  );
  const showSkeleton = isInitialLoading || showDelayedSkeleton;

  const results = data?.results || [];
  const totalCount = data?.count || 0;

  useEffect(() => {
    if (!data) return;
    const lastPage = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));
    if (page > lastPage) setPage(lastPage);
  }, [data, totalCount, page]);

  // Complete Task Mutation
  const completeTaskMutation = useMutation({
    mutationFn: ({ contactId, taskId }) =>
      completeCRMContactTask({ companyId, contactId, taskId }),
    onSuccess: () => {
      toast.success('Task status updated successfully!');
      queryClient.invalidateQueries({ queryKey: ['crm-tasks-and-followups'] });
      queryClient.invalidateQueries({ queryKey: ['crm-recent-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['crm-contact-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['crm-contact-tasks-stats'] });
      queryClient.invalidateQueries({ queryKey: ['crm-recent-unified'] });
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to update task status.');
    },
  });

  // Delete Task Mutation
  const deleteTaskMutation = useMutation({
    mutationFn: ({ contactId, taskId }) =>
      deleteCRMContactTask({ companyId, contactId, taskId }),
    onSuccess: () => {
      toast.success('Task deleted successfully!');
      setTaskToDelete(null);
      queryClient.invalidateQueries({ queryKey: ['crm-tasks-and-followups'] });
      queryClient.invalidateQueries({ queryKey: ['crm-recent-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['crm-contact-tasks'] });
      queryClient.invalidateQueries({ queryKey: ['crm-contact-tasks-stats'] });
      queryClient.invalidateQueries({ queryKey: ['crm-recent-unified'] });
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to delete task.');
    },
  });

  // Delete Follow-up Mutation
  const deleteFollowUpMutation = useMutation({
    mutationFn: ({ contactId, followUpId }) =>
      deleteCRMContactFollowUp({ companyId, contactId, followUpId }),
    onSuccess: () => {
      toast.success('Follow-up deleted successfully!');
      setFollowUpToDelete(null);
      queryClient.invalidateQueries({ queryKey: ['crm-tasks-and-followups'] });
      queryClient.invalidateQueries({ queryKey: ['crm-recent-followups'] });
      queryClient.invalidateQueries({ queryKey: ['crm-contact-followups'] });
      queryClient.invalidateQueries({ queryKey: ['crm-recent-unified'] });
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to delete follow-up.');
    },
  });

  // Pause Follow-up Mutation
  const { mutate: handlePauseFollowUp, isPending: isPausing } = useMutation({
    mutationFn: ({ contactId, followUpId }) =>
      pauseCRMContactFollowUp({ companyId, contactId, followUpId }),
    onSuccess: () => {
      toast.success('Follow-up paused successfully!');
      setActionPopup(null);
      queryClient.invalidateQueries({ queryKey: ['crm-tasks-and-followups'] });
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to pause follow-up.');
    },
  });

  // Resume Follow-up Mutation
  const { mutate: handleResumeFollowUp, isPending: isResuming } = useMutation({
    mutationFn: ({ contactId, followUpId }) =>
      resumeCRMContactFollowUp({ companyId, contactId, followUpId }),
    onSuccess: () => {
      toast.success('Follow-up resumed successfully!');
      setActionPopup(null);
      queryClient.invalidateQueries({ queryKey: ['crm-tasks-and-followups'] });
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to resume follow-up.');
    },
  });

  // Stop Follow-up Mutation
  const { mutate: handleStopFollowUp, isPending: isStopping } = useMutation({
    mutationFn: ({ contactId, followUpId }) =>
      stopCRMContactFollowUp({ companyId, contactId, followUpId }),
    onSuccess: () => {
      toast.success('Follow-up stopped successfully!');
      setActionPopup(null);
      queryClient.invalidateQueries({ queryKey: ['crm-tasks-and-followups'] });
    },
    onError: (err) => {
      const { message } = parseApiError(err);
      toast.error(message || 'Failed to stop follow-up.');
    },
  });

  // Reschedule Follow-up Mutation
  const { mutate: handleRescheduleFollowUp, isPending: isRescheduling } =
    useMutation({
      mutationFn: ({ contactId, followUpId, payload }) =>
        rescheduleCRMContactFollowUp({
          companyId,
          contactId,
          followUpId,
          payload,
        }),
      onSuccess: () => {
        toast.success('Follow-up rescheduled successfully!');
        setActionPopup(null);
        queryClient.invalidateQueries({
          queryKey: ['crm-tasks-and-followups'],
        });
      },
      onError: (err) => {
        const { message } = parseApiError(err);
        toast.error(message || 'Failed to reschedule follow-up.');
      },
    });

  const handleRowClick = (item) => {
    if (item.contact?.id) {
      const contactId = String(item.contact.id);
      const contactName = item.contact.name || '';
      const tabParam =
        item.item_type === 'task' || initialType === 'task'
          ? 'tasks'
          : 'follow-ups';
      try {
        sessionStorage.setItem('crm-contact-overview-id', contactId);
        sessionStorage.setItem('crm-contact-overview-name', contactName);
        window.dispatchEvent(new Event('crm-contact-overview-updated'));
      } catch {
        // ignore storage errors
      }
      navigate(`/crm-contacts/overview?tab=${tabParam}`, {
        state: { contactId, contactName, from: location.pathname },
      });
    }
  };

  const columns = useMemo(
    () =>
      buildTasksFollowupsColumns({
        initialType,
        page,
        onComplete: (vars) => completeTaskMutation.mutate(vars),
        onEditTask: setTaskToEdit,
        onDeleteTask: setTaskToDelete,
        completePending: completeTaskMutation.isPending,
        onAction: setActionPopup,
        onViewFollowUp: setViewingFollowUpItem,
        onDeleteFollowUp: setFollowUpToDelete,
        busy: {
          stop: isStopping,
          reschedule: isRescheduling,
          resume: isResuming,
          pause: isPausing,
        },
      }),
    [
      initialType,
      page,
      completeTaskMutation.isPending,
      isStopping,
      isRescheduling,
      isResuming,
      isPausing,
    ],
  );

  const emptyText =
    initialType === 'task'
      ? 'No tasks found'
      : initialType === 'follow_up'
        ? 'No follow-ups found'
        : 'No records found';

  return (
    <DashboardPage className="crm-list-page h-full flex-1 min-h-0 flex flex-col overflow-hidden">
      <div className="mb-4 flex w-full min-w-0 shrink-0 flex-col gap-3">
        <div className="flex flex-col lg:flex-row items-stretch lg:items-center justify-between gap-3">
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 flex-1 min-w-0">
            <SearchInput
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={
                initialType === 'task'
                  ? 'Search by title, message, or contact name...'
                  : 'Search by template name, message, or contact name...'
              }
              className="w-full sm:max-w-md sm:flex-1"
              inputClassName="h-[44px]"
            />
            {initialType === 'task' && (
              <div className="grid grid-cols-3 sm:flex items-center gap-1.5 sm:gap-2 w-full sm:w-auto shrink-0 py-0.5">
                {STAT_FILTERS.map((filter) => {
                  const isActive = status === filter.id;
                  return (
                    <button
                      key={filter.id}
                      type="button"
                      onClick={() => handleStatusChange(filter.id)}
                      className="inline-flex items-center justify-center gap-1.5 sm:gap-2 h-[38px] sm:h-[42px] px-2.5 sm:px-3.5 rounded-[10px] text-xs sm:text-sm font-medium text-[#1F2241] border transition-colors focus:outline-none focus-visible:outline-none outline-none select-none min-w-0 whitespace-nowrap cursor-pointer"
                      style={
                        isActive
                          ? {
                              backgroundColor: `${filter.dot}1A`,
                              borderColor: filter.dot,
                            }
                          : {
                              backgroundColor: '#F5F5F5',
                              borderColor: 'transparent',
                            }
                      }
                    >
                      <span
                        className="w-1.5 h-1.5 sm:w-2 sm:h-2 rounded-full shrink-0"
                        style={{ backgroundColor: filter.dot }}
                      />
                      <span className="truncate">{filter.label}</span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {initialType === 'task' ? (
            <Button
              variant="primary"
              onClick={() => setIsAddModalOpen(true)}
              className="h-[44px] px-4 sm:px-5 rounded-[10px] text-sm font-semibold shrink-0 gap-1.5 cursor-pointer lg:ml-auto"
            >
              <span className="text-lg leading-none">+</span>
              <span>Add Task</span>
            </Button>
          ) : (
            <Button
              variant="primary"
              onClick={() => setIsAddFollowUpModalOpen(true)}
              className="h-[44px] px-4 sm:px-5 rounded-[10px] text-sm font-semibold shrink-0 gap-1.5 cursor-pointer lg:ml-auto"
            >
              <span className="text-lg leading-none">+</span>
              <span>Add Follow-Up</span>
            </Button>
          )}
        </div>
      </div>

      {isError && !data ? (
        <div className="w-full flex-1 p-8 text-center bg-white rounded-2xl border border-red-200 flex flex-col items-center justify-center gap-3">
          <p className="text-sm font-medium text-red-600">
            Failed to load records.
          </p>
          <Button variant="secondary" onClick={() => refetch()}>
            Retry
          </Button>
        </div>
      ) : (
        <V2Table
          columns={columns}
          data={results}
          rowKey={(row, index) => row.data?.id || `${row.item_type}-${index}`}
          storageKey={
            initialType === 'task'
              ? 'crm_tasks_list_column_widths'
              : 'crm_followups_list_column_widths'
          }
          loading={showSkeleton || (!data && !isError)}
          loadingRowCount={PAGE_SIZE}
          empty={emptyText}
          onRowClick={handleRowClick}
          pagination={{
            currentPage: page,
            totalCount,
            itemsPerPage: PAGE_SIZE,
            onChange: setPage,
          }}
        />
      )}

      {/* Task Add / Edit Modal */}
      <CRMTaskModal
        isOpen={isAddModalOpen || !!taskToEdit}
        onClose={() => {
          setIsAddModalOpen(false);
          setTaskToEdit(null);
        }}
        initialData={taskToEdit}
        companyId={companyId}
        onSuccess={() => refetch()}
      />

      {/* Follow-up Add Modal */}
      <CRMFollowUpModal
        isOpen={isAddFollowUpModalOpen}
        onClose={() => setIsAddFollowUpModalOpen(false)}
        companyId={companyId}
        onSuccess={() => refetch()}
      />

      {/* Confirm Delete Task Modal */}
      <ConfirmDeleteModal
        isOpen={!!taskToDelete}
        onCancel={() => setTaskToDelete(null)}
        onConfirm={() => {
          if (taskToDelete?.contact?.id && taskToDelete?.data?.id) {
            deleteTaskMutation.mutate({
              contactId: taskToDelete.contact.id,
              taskId: taskToDelete.data.id,
            });
          }
        }}
        title="Delete Task"
        description="Are you sure you want to delete this task? This action cannot be undone."
        itemName={taskToDelete?.data?.title || 'this task'}
        isPending={deleteTaskMutation.isPending}
      />

      {/* Confirm Delete Follow-up Modal */}
      <ConfirmDeleteModal
        isOpen={!!followUpToDelete}
        onCancel={() => setFollowUpToDelete(null)}
        onConfirm={() => {
          if (followUpToDelete?.contact?.id && followUpToDelete?.data?.id) {
            deleteFollowUpMutation.mutate({
              contactId: followUpToDelete.contact.id,
              followUpId: followUpToDelete.data.id,
            });
          }
        }}
        title="Delete Follow-up"
        description="Are you sure you want to delete this follow-up? This action cannot be undone."
        itemName="this follow-up"
        isPending={deleteFollowUpMutation.isPending}
      />

      {/* Detail View Modal for Follow-up */}
      <CRMFollowUpDetailModal
        companyId={companyId}
        contactId={viewingFollowUpItem?.contact?.id}
        followUpId={viewingFollowUpItem?.data?.id}
        onClose={() => setViewingFollowUpItem(null)}
        isPausing={isPausing}
        isResuming={isResuming}
        isStopping={isStopping}
        onPause={() =>
          setActionPopup({
            type: 'pause',
            contactId: viewingFollowUpItem?.contact?.id,
            followUpId: viewingFollowUpItem?.data?.id,
          })
        }
        onResume={() =>
          setActionPopup({
            type: 'resume',
            contactId: viewingFollowUpItem?.contact?.id,
            followUpId: viewingFollowUpItem?.data?.id,
          })
        }
        onStop={() =>
          setActionPopup({
            type: 'stop',
            contactId: viewingFollowUpItem?.contact?.id,
            followUpId: viewingFollowUpItem?.data?.id,
          })
        }
        onReschedule={() =>
          setActionPopup({
            type: 'reschedule',
            contactId: viewingFollowUpItem?.contact?.id,
            followUpId: viewingFollowUpItem?.data?.id,
          })
        }
      />

      {/* Stop Follow-Up Confirm Modal */}
      <StopFollowUpConfirmModal
        isOpen={actionPopup?.type === 'stop'}
        onCancel={() => {
          if (!isStopping) setActionPopup(null);
        }}
        onConfirm={() =>
          handleStopFollowUp({
            contactId: actionPopup?.contactId,
            followUpId: actionPopup?.followUpId,
          })
        }
        isPending={isStopping}
      />

      {/* Reschedule Modal */}
      <RescheduleModal
        isOpen={actionPopup?.type === 'reschedule'}
        onClose={() => {
          if (!isRescheduling) setActionPopup(null);
        }}
        onReschedule={(newDate) =>
          handleRescheduleFollowUp({
            contactId: actionPopup?.contactId,
            followUpId: actionPopup?.followUpId,
            payload: { new_date_time: newDate },
          })
        }
        isRescheduling={isRescheduling}
      />

      <ActionConfirmModal
        isOpen={actionPopup?.type === 'pause'}
        onCancel={() => {
          if (!isPausing) setActionPopup(null);
        }}
        onConfirm={() =>
          handlePauseFollowUp({
            contactId: actionPopup?.contactId,
            followUpId: actionPopup?.followUpId,
          })
        }
        isPending={isPausing}
        title="Pause Follow-up"
        description="This follow-up will pause until you resume it."
        confirmLabel="Pause"
        pendingLabel="Pausing..."
      />

      <ActionConfirmModal
        isOpen={actionPopup?.type === 'resume'}
        onCancel={() => {
          if (!isResuming) setActionPopup(null);
        }}
        onConfirm={() =>
          handleResumeFollowUp({
            contactId: actionPopup?.contactId,
            followUpId: actionPopup?.followUpId,
          })
        }
        isPending={isResuming}
        title="Resume Follow-up"
        description="This follow-up will start running again on its schedule."
        confirmLabel="Resume"
        pendingLabel="Resuming..."
      />
    </DashboardPage>
  );
}
